import { Divider, Flex } from '@byecode/ui'
import { type DataSourceAbstract, type Field, type SchemaProtocol } from '@lighthouse/core'
import { getViewColumns, Group4ByecodeUi, useAtomData } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { languagePageListAtom } from '@/atoms/page/state'
import { EventList } from '@/components/EventList'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import { ButtonSettingList } from '../../Button/ButtonSettingList'
import { QuickFilterSetting } from '../../Common/QuickFilterSetting'
import { AllowImportSetting, SwitchItem } from '../../Common/UserOperate'

interface CalendarOperateProps {
    id: string
    dataSource: DataSourceAbstract
    isUserDataSource?: boolean
}

export const CalendarOperate: React.FC<CalendarOperateProps> = ({ id, dataSource, isUserDataSource }) => {
    const { watch, control, setValue } = useFormContext()
    const [viewType, viewFieldSettings, dsId, canImport, userImportSetting, triggerWorkflow] = watch([
        'viewType',
        'viewFieldSettings',
        'pointer',
        'canImport',
        'userImportSetting',
        'triggerWorkflow'
    ])
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { schema, viewOptions, sync, type } = dataSource
    const { tableProps } = viewOptions

    const isJoinDataSource = false /* useMemo(() => type === DataSourceType.joinDataSource, [type]) */

    const columns = useMemo(
        () =>
            getViewColumns({
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [schema, tableProps, viewFieldSettings]
    )

    // 合并数据源与视图上的字段
    const mergedSchema = useMemo(() => {
        return columns.reduce<SchemaProtocol['schema']>((acc, item) => {
            acc[item.fieldId] = { ...item, id: item.fieldId, name: item.title } as Field
            return acc
        }, {})
    }, [columns])

    return (
        <>
            <Group4ByecodeUi
                label={
                    <PopoverQuestion
                        type="toolOperation"
                        arrowOffset={20}
                        renderTarget={(node, ref) => (
                            <Flex gap={4} alignItems="center" ref={ref}>
                                工具栏操作{node}
                            </Flex>
                        )}
                    />
                }
            >
                <div style={{ padding: '0 8px' }}>
                    <ButtonSettingList prefixName="toolbar" hideConfigs={{ fillWay: true }} />
                </div>
            </Group4ByecodeUi>

            <Divider color="var(--color-gray-200)" />

            {/* 2023-09-10 07:39:42 确定日历视图无行操作 */}
            {/* <Group4ByecodeUi label="行操作">
                <EditRecord
                    viewId={id}
                    value={{ canViewEdit, editingConfig }}
                    isCreatePage
                    dsId={dsId}
                    onChange={val => {
                        const { canViewEdit, editingConfig } = val
                        setValue('canViewEdit', canViewEdit)
                        setValue('editingConfig', editingConfig)
                    }}
                />
                <Controller
                    control={control}
                    name="canDeleteRecord"
                    render={({ field }) => (
                        <SwitchItem
                            disabled={!dataSource || dataSource.type === 2}
                            icon="Trash"
                            label="删除"
                            value={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            </Group4ByecodeUi>

            <Divider color="var(--color-gray-200)" /> */}

            <Controller
                control={control}
                name="eventList"
                render={({ field }) => (
                    <EventList blockType="view" view={{ viewType, viewDsId: dsId }} value={field.value} onChange={field.onChange} />
                )}
            />

            <Divider color="var(--color-gray-200)" />

            <Group4ByecodeUi label="其它操作">
                <Controller
                    control={control}
                    name="canCreateRecord"
                    render={({ field: { value, onChange } }) => (
                        <SwitchItem icon="PropertyCreatCalendar" label="添加日程" value={value} onChange={onChange} />
                    )}
                />

                {!isJoinDataSource && (
                    <AllowImportSetting
                        type="UploadSimple"
                        title="批量导入"
                        columns={columns}
                        value={{ canImport, userImportSetting, triggerWorkflow }}
                        dataSource={dataSource}
                        dataSourceList={dataSourceList}
                        onChange={val => {
                            const { canImport, userImportSetting, triggerWorkflow } = val
                            setValue('canImport', canImport)
                            setValue('userImportSetting', userImportSetting)
                            setValue('triggerWorkflow', triggerWorkflow)
                        }}
                    />
                )}

                <Controller
                    control={control}
                    name="canSearch"
                    render={({ field }) => <SwitchItem icon="SearchLine" label="搜索数据" value={field.value} onChange={field.onChange} />}
                />

                <QuickFilterSetting schema={mergedSchema} dataSourceList={dataSourceList} columns={columns} />
                <Controller
                    control={control}
                    name="canGroup"
                    render={({ field }) => <SwitchItem icon="GroupBy" label="设置分组" value={field.value} onChange={field.onChange} />}
                />
            </Group4ByecodeUi>
        </>
    )
}
